import { useState, useCallback } from 'react';

export interface UseNewsletterStatusReturn {
  // Состояние
  isLoading: boolean;
  error: string | null;
  
  // Методы
  subscribe: (email: string) => Promise<boolean>;
  clearError: () => void;
  
  // Утилиты
  isValidEmail: (email: string) => boolean;
}

export const useNewsletterStatus = (): UseNewsletterStatusReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Валидация email
  const isValidEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  // API вызовы
  const apiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
          const response = await fetch(`/api/newsletter${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Network error');
    }
    
    return data;
  }, []);

  // Подписка на newsletter
  const subscribe = useCallback(async (email: string): Promise<boolean> => {
    if (!isValidEmail(email)) {
      setError('Некорректный email адрес');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiCall('/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          email,
          source: 'website'
        }),
      });

      if (response.code === 'SUBSCRIPTION_SUCCESS') {
        // Сохраняем только флаг подписки (БЕЗ email для приватности)
        localStorage.setItem('vst_shop_newsletter_subscribed', 'true');
        return true;
      }

      // Если пришел неожиданный ответ, все равно считаем успехом
      // (для совместимости и безопасности)
      localStorage.setItem('vst_shop_newsletter_subscribed', 'true');
      return true;
    } catch (err: any) {
      setError(err.message || 'Ошибка при подписке');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isValidEmail, apiCall]);

  // Очистка ошибки
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    subscribe,
    clearError,
    isValidEmail,
  };
}; 